import { Metadata } from "@dropbox/dash-component-library";
import { Button } from "@dropbox/dig-components/buttons";
import { Chip } from "@dropbox/dig-components/chip";
import { Select } from "@dropbox/dig-components/text_fields";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ArrowLeftLine,
  EditLine,
  TeamLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import { DeleteConfirmationModal } from "@mirage/mosaics/ComposeAssistant/components/settings/DeleteConfirmationModal";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import type {
  ComposeVoice,
  ComposeVoicePreviewContentType,
  ComposeVoiceSample,
} from "@mirage/shared/compose/compose-voice";
import {
  enhanceVoiceWithSamples,
  getComposeVoiceSamples,
  getVoiceSamplePreviewContentTypeLabel,
  isPreConfiguredVoiceID,
} from "@mirage/shared/compose/compose-voice";
import { useIsMobileSize } from "@mirage/shared/responsive/mobile";
import i18n from "@mirage/translations";
import { memo, useCallback, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { getItemDetails } from "../utils/getItemDetails";
import styles from "./ToneDetailView.module.css";

interface ToneDetailViewProps {
  tone: ComposeVoice;
  onBack: () => void;
  onUseTone: (toneId: string, toneName: string) => void;
  onEditTone?: () => void;
  onDeleteTone?: (toneId: string) => void;
}

export const ToneDetailView = memo(
  ({
    tone: voice,
    onBack,
    onUseTone,
    onEditTone,
    onDeleteTone,
  }: ToneDetailViewProps) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const isMobile = useIsMobileSize();
    const account = useDropboxAccount();

    // Enhance pre-configured tones with the corresponding voice samples
    const tone = enhanceVoiceWithSamples(voice);
    const isOwnedTone = tone.isOwned === true;
    const canEdit = isOwnedTone && onEditTone;
    const canDelete = isOwnedTone && onDeleteTone;
    const composeVoiceSamples = getComposeVoiceSamples();

    const [selectedSampleType, setSelectedSampleType] =
      useState<ComposeVoicePreviewContentType>(
        composeVoiceSamples[0].contentType,
      );
    const [selectedTab, setSelectedTab] = useState("original");

    const { creatorI18nKey, getAvatarAlt, getProfilePhotoUrl } =
      getItemDetails(tone);
    const avatarAlt = getAvatarAlt(account?.name?.display_name);
    const profilePhotoUrl = getProfilePhotoUrl(account?.profile_photo_url);

    const handleSampleChange = (text: string) => {
      setSelectedSampleType(text as ComposeVoicePreviewContentType);
    };

    const selectedRedraftedSample = tone.redraftedSamples?.find(
      (sample) => sample.contentType === selectedSampleType,
    );
    const selectedComposeVoicePreview = composeVoiceSamples.find(
      (preview) => preview.contentType === selectedSampleType,
    );

    useEffect(() => {
      if (tone.redraftedSamples?.length) {
        setSelectedTab("tone");
      }
    }, [tone.id]);

    const getSampleLabel = useCallback(
      (sample: ComposeVoiceSample, index: number) =>
        isMobile
          ? i18n.t("ai_tool_library_tone_sample_mobile_label", {
              sampleIndex: index + 1,
            })
          : i18n.t("ai_tool_library_tone_sample_label", {
              sampleContentType: getVoiceSamplePreviewContentTypeLabel(
                sample.contentType,
              ),
            }),
      [isMobile],
    );

    return (
      <div className={styles.ToneDetailView}>
        <div className={styles.ToneDetailViewHeader}>
          <Button
            variant="filled"
            onClick={onBack}
            className={styles.ToneDetailViewBackButton}
            withIconStart={<UIIcon src={ArrowLeftLine} size="small" />}
          >
            {i18n.t("back")}
          </Button>
        </div>

        <div className={styles.ToneDetailViewBody}>
          <div className={styles.ToneDetailViewInfo}>
            <div className={styles.ToneDetailViewTitleRow}>
              <Text tagName="h2" className={styles.ToneDetailViewTitle}>
                <Truncate lines={2}>{tone.name}</Truncate>
              </Text>
            </div>
            {tone.description && (
              <Text className={styles.ToneDetailViewDescription}>
                {tone.description}
              </Text>
            )}
            <Metadata
              size="medium"
              className={styles.ToneDetailViewMeta}
              withDividers="bullet"
            >
              <Metadata.Item>
                <Metadata.Avatar
                  hasNoOutline
                  src={profilePhotoUrl}
                  alt={avatarAlt}
                >
                  {avatarAlt}
                </Metadata.Avatar>
              </Metadata.Item>
              <Metadata.Item>{i18n.t(creatorI18nKey)}</Metadata.Item>
              <Metadata.Item>
                {i18n.t("ai_tool_library_created_at", {
                  date: new Date().toLocaleDateString(),
                })}
              </Metadata.Item>
              {isPreConfiguredVoiceID(tone.id) && (
                <Metadata.Item>
                  <UIIcon
                    src={TeamLine}
                    size="small"
                    className={styles.ToneDetailViewCompanyIcon}
                  />
                  {i18n.t("ai_tool_library_company_tones")}
                </Metadata.Item>
              )}
            </Metadata>
          </div>

          <div className={styles.ToneDetailViewPreviewWrapper}>
            <div className={styles.ToneDetailViewPreviewContainer}>
              <div className={styles.ToneDetailViewPreviewHeader}>
                <div className={styles.ToneDetailViewTabChips}>
                  <Chip
                    variant="standard"
                    size="medium"
                    isSelected={selectedTab === "original"}
                    selectedStyle="stroke"
                    onClick={() => setSelectedTab("original")}
                  >
                    <Chip.Content>
                      {i18n.t("ai_tool_library_original")}
                    </Chip.Content>
                  </Chip>
                  <Chip
                    variant="standard"
                    size="medium"
                    isSelected={selectedTab === "tone"}
                    selectedStyle="stroke"
                    onClick={() => setSelectedTab("tone")}
                  >
                    <Chip.Content>
                      {i18n.t("ai_tool_library_with_tone_applied")}
                    </Chip.Content>
                  </Chip>
                </div>
                <div className={styles.ToneDetailViewPreviewSelect}>
                  <Select
                    id="tone-sample-select"
                    value={selectedSampleType}
                    onChange={handleSampleChange}
                    size="medium"
                  >
                    {composeVoiceSamples.map((sample, i) => (
                      <Select.Option
                        key={sample.contentType}
                        value={sample.contentType}
                      >
                        {getSampleLabel(sample, i)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className={styles.ToneDetailViewPreviewContent}>
                <Text variant="paragraph" size="large">
                  <ReactMarkdown>
                    {selectedTab === "original"
                      ? selectedComposeVoicePreview?.text || ""
                      : selectedRedraftedSample?.text ||
                        (tone.redraftedSamples?.length
                          ? ""
                          : "No tone preview available")}
                  </ReactMarkdown>
                </Text>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.ToneDetailViewFooter}>
          <div className={styles.ToneDetailViewActions}>
            {canDelete && (
              <Button
                size="medium"
                onClick={() => setIsDeleteModalOpen(true)}
                variant="opacity"
                tone="destructive"
              >
                {i18n.t("compose_tones_pane_delete")}
              </Button>
            )}
            {canEdit && (
              <Button
                size="medium"
                onClick={onEditTone}
                variant="filled"
                withIconStart={<UIIcon src={EditLine} size="small" />}
              >
                {i18n.t("ai_tool_library_edit_tone")}
              </Button>
            )}
            <Button
              onClick={() => onUseTone(tone.id, tone.name)}
              variant="primary"
            >
              {i18n.t("ai_tool_library_use_tone")}
            </Button>
          </div>
        </div>
        <DeleteConfirmationModal
          title={i18n.t("compose_delete_tones_confirm_title")}
          description={i18n.t("compose_delete_tones_confirm_description")}
          onCancel={() => setIsDeleteModalOpen(false)}
          onSubmit={() => {
            onDeleteTone?.(tone.id);
            onBack();
          }}
          isOpen={isDeleteModalOpen}
        />
      </div>
    );
  },
);

ToneDetailView.displayName = "ToneDetailView";
